import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import iconSearch from "../../../assets/media/icon-search.svg";
import Navbar from "../../navbar";
import Header from "../../header";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "../dashboard/appPagination";
import {
  createCompany,
  deleteCompany,
  restoreCompany,
  fetchCompanies,
  updateCompany,
  resendEmail,
  reactivateUser,
} from "../../../store/reducers/companiesSlice.js";
import { addEmployee } from "../../../store/reducers/employeeSlice.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import PhoneInput from "react-phone-input-2";
import { Button } from "react-bootstrap";
import ConfirmationModal from "../../common/confirmationModal.jsx";
import Modal from "../../modal/Modal";
import { downloadUsersCsv } from "../../../helpers/csvExport";
import { notifyApiError, notifyApiSuccess } from "../../../helpers/apiNotify";
import { canAmendRegistry } from "../../../utility/rbac";

const columnHelper = createColumnHelper();

const Companies = () => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const canAmend = canAmendRegistry(user);
  const createLoading = useSelector((state) => state.employee.createLoading);
  const companiesLoading = useSelector((state) => state.companies.loading);
  // List fetch + create company both use companies.loading
  const fetchLoading = companiesLoading && !createLoading;
  const isCreatingCompany = Boolean(companiesLoading || createLoading);
  const [selected_Company, setSelected_Company] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchBy, setSearchBy] = useState("company_id");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  const [isRestore, setIsRestore] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [phoneInputKey, setPhoneInputKey] = useState(0);
  const [editPhoneInputKey, setEditPhoneInputKey] = useState(0);

  const authToken = user.access_token;

  const handleDownloadCompaniesCsv = async () => {
    if (!authToken || downloadingCsv) return;
    setDownloadingCsv(true);
    try {
      await downloadUsersCsv({
        accessToken: authToken,
        roles: "company_admin",
        fileName: "companies-export",
      });
      notifyApiSuccess("Companies CSV downloaded");
    } catch (err) {
      notifyApiError(err, "Failed to download companies CSV");
    } finally {
      setDownloadingCsv(false);
    }
  };

  const initialValues = {
    national_id_number: "",
    company_name: "",
    no_of_employee: "",
    company_email: "",
    registration_date: null,
    phone_no: "",
  };

  const validationSchema = Yup.object({
    national_id_number: Yup.string().required("Registration No is required"),
    company_name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .required("Full Name is required"),
    company_email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    no_of_employee: Yup.string().required("Number of employees is required"),
    registration_date: Yup.mixed().required("Registration date is required"),
    phone_no: Yup.string().required("Phone number is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const {
        company_email,
        national_id_number,
        company_name,
        no_of_employee,
        registration_date,
        phone_no,
      } = values;
      const formattedDate = moment(registration_date)
        .add(1, "days")
        .format("YYYY/MM/DD");

      try {
        const created = await dispatch(
          createCompany({
            companyData: {
              company_id: national_id_number,
              company_name,
              company_email: company_email?.toLowerCase()?.trim(),
              no_of_employee,
              registration_date: formattedDate,
              phone_no,
            },
            accessToken: authToken,
          })
        ).unwrap();

        const companyId =
          created?.id ?? created?.company?.id ?? created?.data?.id;
        if (!companyId) {
          throw new Error("Company created but no id was returned");
        }

        await dispatch(
          addEmployee({
            userData: {
              full_name: company_name,
              national_id_number: national_id_number,
              email: company_email?.toLowerCase()?.trim(),
              phone: phone_no,
              roles: [
                "create_discrepencies",
                "issue_certificates",
                "create_forms",
                "add_delete_users",
              ],
              is_admin: false,
              company_id: companyId,
              user_role: "company_admin",
            },
            accessToken: authToken,
          })
        ).unwrap();

        fetchcompanydata(currentPage, searchQuery);
        resetForm();
        // Remount PhoneInput — it stops accepting input after value resets to ""
        setPhoneInputKey((key) => key + 1);
      } catch {
        // API layer already shows toasts
      }
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  const formikEdit = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const {
        company_email,
        national_id_number,
        company_name,
        no_of_employee,
        registration_date,
        phone_no,
      } = values;
      const formattedDate = moment(registration_date)
        .add(1, "days")
        .format("YYYY/MM/DD");

      if (selected_Company) {
        dispatch(
          updateCompany({
            companyId: selected_Company.company_db_id,
            companyData: {
              id: selected_Company.company_db_id,
              company_id: national_id_number,
              company_name,
              company_email: company_email?.toLowerCase()?.trim(),
              no_of_employee,
              registration_date: formattedDate,
              phone_no,
            },
            accessToken: authToken,
          })
        ).then(async (res) => {
          if (!res.error) {
            try {
              await dispatch(
                reactivateUser({
                  email: company_email?.toLowerCase()?.trim(),
                  id: selected_Company.id,
                  accessToken: authToken,
                })
              ).unwrap();
            } catch {
              // API layer already shows toasts
            }
          }
          setShowModal(false);
          setSelected_Company(null);
          formikEdit.resetForm();
          setEditPhoneInputKey((key) => key + 1);
          fetchcompanydata(currentPage, searchQuery);
        });
      }
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  const handleEditClick = useCallback((data) => {
    setSelected_Company({
      id: data.id, // The first id
      company_db_id: data.company?.id || data.company_id || "",
      national_id_number: data.national_id_number || data.company_id || "",
      company_name: data.company?.company_name || data.full_name || "",
      company_email: data.email || "",
      no_of_employee: data.company?.no_of_employee || "",
      registration_date: data.company?.registration_date || data.created_at || "",
      phone_no: data.phone || "",
    });
    formikEdit.setValues({
      id: data.id,
      national_id_number: data.national_id_number || data.company_id || "",
      company_name: data.company?.company_name || data.full_name || "",
      company_email: data.email || "",
      no_of_employee: data.company?.no_of_employee || "",
      registration_date: (data.company?.registration_date || data.created_at) ? new Date(data.company?.registration_date || data.created_at) : null,
      phone_no: data.phone || "",
    });
    setEditPhoneInputKey((key) => key + 1);
    setShowModal(true);
  }, [formikEdit]);

  const { values, handleChange, handleSubmit, handleBlur, errors } = formik;

  const company = useSelector((state) => state.companies.companies);
  const pagination = useSelector((state) => state.companies.pagination);
  const fetchcompanydata = (page = 1, search = "") => {
    let queryParams = {
      page: page,
      limit: 10,

    };

    if (search && search.trim() !== "") {
      queryParams[searchBy] = search.trim();
    }

    if (startDate && endDate) {
      queryParams.start_date = moment(startDate).format("YYYY-MM-DD");
      queryParams.end_date = moment(endDate).format("YYYY-MM-DD");
    }
    dispatch(fetchCompanies({ accessToken: authToken, queryParams }));
  };

  const deletecompanydata = async (id, isRestore = false) => {
    const config = {
      accessToken: authToken,
      company_id: id,
    };
    setSelected_Company(null);
    try {
      if (isRestore) {
        await dispatch(restoreCompany({ configdata: config })).unwrap();
      } else {
        await dispatch(deleteCompany({ configdata: config })).unwrap();
      }
      fetchcompanydata(currentPage, searchQuery);
    } catch {
      // API layer already shows toasts
    }
  };

  useEffect(() => {
    fetchcompanydata(currentPage, searchQuery);
  }, [authToken, dispatch, dateRange, currentPage, searchQuery, searchBy]);

  const getTotalPages = () => {
    // Use server-side pagination metadata
    if (pagination?.totalPages) {
      return pagination.totalPages;
    }
    // Fallback to client-side calculation
    return Math.ceil(company.length / 10);
  };

  const handleSearchInputChange = (e) => {
    let value = e.target.value;
    if (searchBy === "companyId" || searchBy === "company_id") {
      value = value.replace(/^gb-?/i, "").replace(/\D/g, "");
    }
    setSearchQuery(value);
    // Reset to page 1 when searching
    setCurrentPage(1);
  };

  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
    setSearchQuery("");
  };

  const handleResendEmail = async (userData) => {
    const payload = {
      email: userData.email, // Use email from the object
      id: userData.id, // The first id
      accessToken: user.access_token
    }
    try {
      await dispatch(reactivateUser(payload)).unwrap();
      fetchcompanydata(currentPage, searchQuery);
    } catch {
      // API layer already shows toasts
    }
  }

  // Since we're using server-side pagination, we don't need client-side filtering
  // The server should handle search filtering
  const filteredCompanies = company || [];

  const paginatedCompanies = useMemo(() => {
    if (filteredCompanies.length > 10) {
      const startIndex = (currentPage - 1) * 10;
      return filteredCompanies.slice(startIndex, startIndex + 10);
    }
    return filteredCompanies;
  }, [filteredCompanies, currentPage]);

  // React Table columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => {
          const startIndex = (currentPage - 1) * 10;
          return startIndex + info.row.index + 1;
        },
      }),
      columnHelper.accessor("national_id_number", {
        header: "Registration No",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {info.getValue() || info.row.original.national_id_number}
          </span>
        ),
      }),
      columnHelper.accessor("company.company_name", {
        header: "Company Name",
        cell: (info) => {
          return (
            <div className="boardTitle widAvatar">
              <strong style={{ whiteSpace: "nowrap" }}>
                {info.getValue() || info.row.original.company?.company_name}
              </strong>
            </div>
          )
        }
      }),
      columnHelper.accessor("email", {
        header: "Company Email",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {info.getValue() || info.row.original.company?.company_email}
          </span>
        ),
      }),
      columnHelper.accessor("company.no_of_employee", {
        header: "No Of Employee",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {info.getValue() || info.row.original.no_of_employee || "—"}
          </span>
        ),
      }),
      columnHelper.accessor("created_at", {
        header: "Registration Date",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {info.getValue()?.slice(0, 10) || info.row.original.company?.registration_date?.slice(0, 10) || ""}
          </span>
        ),
      }),
      columnHelper.accessor("phone", {
        header: "Phone No.",
        cell: (info) => (
          <span style={{ whiteSpace: "nowrap" }}>
            {info.getValue() || info.row.original.company?.phone_no}
          </span>
        ),
      }),
      columnHelper.accessor("active", {
        header: "Status",
        cell: (info) => {
          const { active, is_deleted } = info.row.original;
          return active && !is_deleted ? (
            <span className="reasonText boardButton status-approved">Active</span>
          ) : (
            <span className="reasonText boardButton status-rejected">Inactive</span>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const item = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  {canAmend && !item.active && (
                    <li>
                      <Link
                        onClick={() => handleResendEmail(item)}
                        className="dropdown-item"
                      >
                        Resend Email
                      </Link>
                    </li>
                  )}

                  {canAmend && (
                    <li>
                      <Link
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowDeleteModal(true);
                          // Prefer company DB id for DELETE / PATCH restore
                          setDeleteUserId(
                            item.company?.id || item.company_id || item.id
                          );
                          setIsRestore(item.is_deleted);
                        }}
                        className="dropdown-item"
                      >
                        {item.is_deleted ? "Restore" : "Delete"}
                      </Link>
                    </li>
                  )}

                  {item.active ? (
                    <li>
                      <Link
                        to={`/companies/${item.company_id}`}
                        className="dropdown-item"
                      >
                        View
                      </Link>
                    </li>
                  ) : (
                    canAmend && (
                      <li>
                        <Link
                          onClick={(e) => {
                            e.preventDefault();
                            handleEditClick(item);
                          }}
                          className="dropdown-item"
                        >
                          Edit
                        </Link>
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [currentPage, handleResendEmail, handleEditClick, canAmend]
  );

  const table = useReactTable({
    data: paginatedCompanies,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          <div className="boardHeader noMg">
            <div className="boardHeadLeft">
              <h2>Registered Companies</h2>
            </div>
            <div className="boardHeadRight">
                <Button
                  variant="primary"
                  onClick={handleDownloadCompaniesCsv}
                  disabled={downloadingCsv}
                >
                  {downloadingCsv ? "Downloading…" : "Download"}
                </Button>
            </div>
          </div>
        </div>
        {canAmend && (
        <div className="empAdd">
          <h3 className="noMg">Add Company </h3>
          <form className="addEmployee-form" onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>Registration No</label>
                  <input
                    name="national_id_number"
                    type="text"
                    placeholder="GB-4936209"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.national_id_number}
                    error={errors.national_id_number}
                  />
                  {errors.national_id_number && (
                    <div className="error-message">{errors.national_id_number}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>Company Name</label>
                  <input
                    name="company_name"
                    type="text"
                    placeholder="Abc Company"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.company_name}
                    error={errors.company_name}
                  />
                  {errors.company_name && (
                    <div className="error-message">{errors.company_name}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>Company Email</label>
                  <input
                    name="company_email"
                    type="email"
                    placeholder="abc@gmail.com"
                    onChange={(e) => {
                      e.target.value = e.target.value.toLowerCase();
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                    value={values.company_email}
                    error={errors.company_email}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                  {errors.company_email && (
                    <div className="error-message">{errors.company_email}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>No of employee</label>
                  <input
                    name="no_of_employee"
                    type="text"
                    placeholder="1,2,3....."
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.no_of_employee}
                    error={errors.no_of_employee}
                  />
                  {errors.no_of_employee && (
                    <div className="error-message">{errors.no_of_employee}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>Registration Date</label>
                  <DatePicker
                    name="registration_date"
                    selected={values.registration_date}
                    onChange={(date) =>
                      formik.setFieldValue("registration_date", date)
                    }
                    onBlur={handleBlur}
                    dateFormat="yyyy/MM/dd"
                    placeholderText="Select a date"
                    className="form-control datepicker-container"
                  />
                  {errors.registration_date && (
                    <div className="error-message">
                      {errors.registration_date}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="fieldWrap">
                  <label>Phone No.</label>
                  <PhoneInput
                    key={phoneInputKey}
                    country={"bs"}
                    value={values.phone_no}
                    onChange={(phone) =>
                      formik.setFieldValue("phone_no", phone)
                    }
                    onBlur={handleBlur}
                    placeholder="(242) 555-0122"
                    countryCodeEditable={false}
                    masks={{ bs: '...-....' }}
                    inputProps={{
                      name: "phone_no",
                    }}
                    className={errors.phone_no ? "error-input" : ""}
                  />
                  {errors.phone_no && (
                    <div className="error-message">{errors.phone_no}</div>
                  )}
                </div>
              </div>

              <div className="d-flex align-items-end justify-content-between mt-5">
                <div className="boardHeadRight">
                  <div className="boardFilter"></div>
                </div>

                <div className="">
                  <div className="fieldWrap noLabel">
                    <button
                      type="submit"
                      disabled={isCreatingCompany}
                    >
                      {isCreatingCompany ? (
                        <>
                          <div
                            className="text-xs spinner-border text-light"
                            style={{ width: "1rem", height: "1rem" }}
                            role="status"
                          ></div>
                          <span className="sr-only ml-5"> Loading...</span>
                        </>
                      ) : (
                        "Add Company"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        )}
        <div className="boxAppBoard boxWhite mt-5">
          <div
            style={{ flexWrap: "wrap", gap: "10px" }}
            className="boardHeader"
          >
            <div className="boardHeadLeft">
              <h2>All Companies</h2>
            </div>
            <div
              style={{
                gap: "10px",
                flexWrap: "wrap",
                justifyContent: "flex-start",
              }}
              className="boardHeadRight"
            >
              <div className="">
                <select onChange={handleSearchByChange} value={searchBy}>
                  <option value="companyId">Search by ID</option>
                  <option value="companyName">Search by Name</option>
                  <option value="email">Search by Email </option>
                  <option value="phone">Search by Phone </option>
                </select>
              </div>
              <div className="searchForm">
                <input
                  type="text"
                  placeholder={(searchBy === "companyId" || searchBy === "company_id") ? "GB-XXX" : "Search"}
                  onChange={handleSearchInputChange}
                  value={(searchBy === "companyId" || searchBy === "company_id") && searchQuery ? `GB-${searchQuery}` : searchQuery}
                />
                <button>
                  <img src={iconSearch} alt="" />
                </button>
              </div>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    setDateRange(update);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="yyyy-MM-dd"
                />
              </div>
            </div>
          </div>
          <div className="boardContent">
            <div className="table-responsive">
              <table className="table">
                <thead className="boardTheadcompany">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {fetchLoading ? (
                    <tr>
                      <td
                        className="text-center"
                        colSpan={table.getHeaderGroups()?.[0]?.headers.length || 9}
                      >
                        <div className="d-flex justify-content-center my-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : table.getRowModel().rows.length > 0 ? (
                    table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="boardTrowcompany">
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={table.getHeaderGroups()?.[0]?.headers.length || 9}
                        className="text-center"
                      >
                        <h2 className="text-danger my-4 py-4">
                          Companies not Found
                        </h2>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
          />

        </div>
      </div>
      <Modal show={showModal} handleClose={() => setShowModal(false)} colWidth={6}>
        <div className="modal-header">
          <h5 className="modal-title">Edit Company</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowModal(false)}
          ></button>
        </div>
        <div className="modal-body">
          <form className="addEmployee-form empAdd" onSubmit={formikEdit.handleSubmit}>
            <div className="row">
              <div className="col-12 col-lg-6">
                <div className="fieldWrap d-flex flex-column align-items-start">
                  <label>Registration No</label>
                  <input
                    name="national_id_number"
                    type="text"
                    onChange={formikEdit.handleChange}
                    onBlur={formikEdit.handleBlur}
                    value={formikEdit.values.national_id_number}
                  />
                  {formikEdit.errors.national_id_number && (
                    <div className="error-message">{formikEdit.errors.national_id_number}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="fieldWrap d-flex flex-column align-items-start">
                  <label>Company Name</label>
                  <input
                    name="company_name"
                    type="text"
                    placeholder="Abc Company"
                    onChange={formikEdit.handleChange}
                    onBlur={formikEdit.handleBlur}
                    value={formikEdit.values.company_name}
                  />
                  {formikEdit.errors.company_name && (
                    <div className="error-message">{formikEdit.errors.company_name}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="fieldWrap d-flex flex-column align-items-start">
                  <label>Company Email</label>
                  <input
                    name="company_email"
                    type="email"
                    placeholder="abc@gmail.com"
                    onChange={(e) => {
                      e.target.value = e.target.value.toLowerCase();
                      formikEdit.handleChange(e);
                    }}
                    onBlur={formikEdit.handleBlur}
                    value={formikEdit.values.company_email}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                  {formikEdit.errors.company_email && (
                    <div className="error-message">{formikEdit.errors.company_email}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="fieldWrap d-flex flex-column align-items-start">
                  <label>No of employee</label>
                  <input
                    name="no_of_employee"
                    type="text"
                    placeholder="1,2,3....."
                    onChange={formikEdit.handleChange}
                    onBlur={formikEdit.handleBlur}
                    value={formikEdit.values.no_of_employee}
                  />
                  {formikEdit.errors.no_of_employee && (
                    <div className="error-message">{formikEdit.errors.no_of_employee}</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="fieldWrap d-flex flex-column align-items-start">
                  <label>Registration Date</label>
                  <DatePicker
                    name="registration_date"
                    selected={formikEdit.values.registration_date}
                    onChange={(date) =>
                      formikEdit.setFieldValue("registration_date", date)
                    }
                    onBlur={formikEdit.handleBlur}
                    dateFormat="yyyy/MM/dd"
                    placeholderText="Select a date"
                    className="form-control datepicker-container"
                  />
                  {formikEdit.errors.registration_date && (
                    <div className="error-message">
                      {formikEdit.errors.registration_date}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12 col-lg-6">
                <div className="fieldWrap d-flex flex-column align-items-start">
                  <label>Phone No.</label>
                  <PhoneInput
                    key={editPhoneInputKey}
                    country={"bs"}
                    value={formikEdit.values.phone_no}
                    onChange={(phone) =>
                      formikEdit.setFieldValue("phone_no", phone)
                    }
                    onBlur={formikEdit.handleBlur}
                    placeholder="(242) 555-0122"
                    countryCodeEditable={false}
                    masks={{ bs: '...-....' }}
                    inputProps={{
                      name: "phone_no",
                    }}
                    className={formikEdit.errors.phone_no ? "error-input" : ""}
                  />
                  {formikEdit.errors.phone_no && (
                    <div className="error-message">{formikEdit.errors.phone_no}</div>
                  )}
                </div>
              </div>
              <div className="col-12 ">
                <div className="modal-footer" style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={companiesLoading}
                  >
                    {companiesLoading ? "Updating..." : "Update Company"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </Modal >
      <ConfirmationModal
        show={showDeleteModal && deleteUserId}
        title={`${isRestore ? "Restore" : "Delete"} Company`}
        description={`Are you sure you want to ${isRestore ? "restore" : "delete"
          } this company?`}
        handleClose={() => {
          setShowDeleteModal(false);
          setDeleteUserId(null);
          setIsRestore(false);
        }}
        onConfirm={() => {
          deletecompanydata(deleteUserId, isRestore);
          setShowDeleteModal(false);
          setDeleteUserId(null);
          setIsRestore(false);
        }}
      />
    </div >
  );
};
export default Companies;
